// LandingPage.jsx
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useMessagingPhase } from '../contexts/MessagingPhaseContext'; // Ensure this path is correct
import AnimatedBanner from "./AnimatedBanner";
import GlonetNewLogo from '../assets/GlonetNewLogo.png';
import { GA, GaStyle } from './glonetTheme';

const LandingPage = () => {
  const { currentPhase, setMessagingPhase } = useMessagingPhase();
  // Dynamically use the namespace from the context for translations
  const { t } = useTranslation(currentPhase);

  const location = useLocation();
  const { from, invitationCode, eventId } = location.state || {};

  // Animated banner messages dynamically based on current phase
  // Fetches keys from the JSON file associated with 'currentPhase'
  const animatedBannerMessages = [
    t('animatedBanner.message1'),
    t('animatedBanner.message2'),
    t('animatedBanner.message3'),
    t('animatedBanner.message4'),
    t('animatedBanner.message5')
    // Add more keys here if your JSON files define them (e.g., message6, message7, message8 for phase0)
    // This filter removes entries where the key was not found (t function returns the key itself)
  ].filter(msg => msg && typeof msg === 'string' && !msg.startsWith('animatedBanner.'));


  // Temporary buttons to switch phase for testing - REMOVE FOR PRODUCTION
  const togglePhase = () => {
    const newPhase = currentPhase === 'phase0_event_centric' ? 'phase1_nexus_centric' : 'phase0_event_centric';
    setMessagingPhase(newPhase); // This will cause a reload via the context's implementation
  };

  // Flag to control visibility of the phase switcher UI. Set to false for production.
  const showPhaseSwitcher = false;

  return (
    <div
      className="min-h-screen py-6 flex flex-col justify-center sm:py-12"
      style={{
        background: GA.bg,
        color: GA.text,
        fontFamily: GA.sans,
        WebkitFontSmoothing: "antialiased",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      <GaStyle />

      {/* ambient hero glow (matches GlonetAiLanding) */}
      <div
        style={{
          position: "absolute",
          top: "-220px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "1000px",
          height: "680px",
          background: "radial-gradient(circle at center, rgba(116,160,245,0.22) 0%, transparent 68%)",
          filter: "blur(14px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Conditionally render the switcher based on the flag */}
      {showPhaseSwitcher && (
        <div style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 1000, background: GA.panel, border: `1px solid ${GA.panelBorder}`, color: GA.text, padding: '10px', borderRadius: '5px', fontSize: '0.8rem' }}>
          <p className="text-xs mb-1">Current Phase: {currentPhase.replace('_centric', '').replace('phase0', 'Phase 0').replace('phase1', 'Phase 1')}</p>
          <button onClick={togglePhase} className="text-xs py-1 px-2 rounded" style={{ background: GA.accent, color: GA.onAccent, fontWeight: 600 }}>
            Switch to {currentPhase === 'phase0_event_centric' ? 'Phase 1 (Nexus)' : 'Phase 0 (Event)'}
          </button>
        </div>
      )}
      {/* End Phase Switcher */}

      <AnimatedBanner
        rounded={false}
        showToggleButton={true}
        messages={animatedBannerMessages.length > 0 ? animatedBannerMessages : undefined}
      />

      <div className="relative py-3 sm:max-w-xl sm:mx-auto mt-4" style={{ zIndex: 2 }}>
        <div
          className="relative px-6 py-10 sm:p-20"
          style={{
            background: GA.soft,
            border: `1px solid ${GA.panelBorder}`,
            borderRadius: "24px",
            boxShadow: "0 24px 60px -30px rgba(0,0,0,0.7)",
          }}
        >
          <div className="max-w-md mx-auto text-center">
            <div className="flex justify-center items-center space-x-4 mb-6">
              <img
                src="/MindMachineLearningLOGO.svg"
                className="h-14 sm:h-20 w-auto"
                alt={t('landingPage.mmlLogoAlt', "MML Logo")}
              />
              <img
                src={GlonetNewLogo}
                className="h-14 sm:h-20 w-auto animate-pulse"
                style={{ borderRadius: "12px" }}
                alt={t('landingPage.glonetLogoAlt', "Glonet Logo")}
              />
            </div>

            <h2
              className="mt-8"
              style={{
                fontFamily: GA.serif,
                fontWeight: 400,
                fontSize: "clamp(34px,5vw,48px)",
                lineHeight: 1.05,
                letterSpacing: "-0.015em",
                color: GA.text,
              }}
            >
              {/* This will display "Glonet Nexus" (Phase 1) or "Glonet" (Phase 0) */}
              {t('landingPage.heroTitle')}
            </h2>
            <p className="mt-4 text-lg" style={{ color: GA.body, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: t('landingPage.heroSubtitle') }} />
          </div>

          {/*
            As per your instruction, the extra explanatory sections (nexusExplainer, etc.)
            are NOT rendered by default on the landing page to keep it lean.
            If phase1_nexus_centric.json contains these keys and you want them displayed
            *only* for phase1, you would add a conditional block:

            {currentPhase === 'phase1_nexus_centric' && (
              <>
                <div className="mt-6 text-center">
                  <p className="text-md italic" style={{ color: "var(--ga-body)" }}>
                    {t('landingPage.nexusExplainer')}
                  </p>
                </div>
                // ... other phase 1 specific sections ...
              </>
            )}
            For now, adhering to the "simplified landing page" directive.
          */}

          <div className="mt-10 space-y-6">
            <div className="space-y-4">
              <Link
                to="/signin"
                state={{ from, invitationCode, eventId }}
                className="ga-btn ga-btn-primary w-full flex justify-center py-3 px-4 text-base"
                style={{
                  color: GA.onAccent,
                  background: GA.accent,
                  border: "none",
                  borderRadius: "999px",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                {t('landingPage.logInButton')}
              </Link>

              <Link
                to="/signup"
                state={{ from, invitationCode, eventId }}
                className="ga-btn ga-btn-ghost w-full flex justify-center py-3 px-4 text-base"
                style={{
                  color: GA.text,
                  background: "transparent",
                  border: "1px solid var(--ga-ghost-border)",
                  borderRadius: "999px",
                  fontWeight: 500,
                  textDecoration: "none",
                }}
              >
                {t('landingPage.signUpButton')}
              </Link>
            </div>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="ga-link text-sm"
              >
                {t('landingPage.forgotPasswordLink')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
